'use client'

/* Slim top bar shown only on mobile (< 1024px). Hosts the hamburger button
   that opens the off-canvas sidebar. The brand mark sits next to it so the
   user always knows what app they're in. */

import Link from 'next/link'
import { Menu } from 'lucide-react'

interface MobileTopbarProps {
  onMenuClick: () => void
}

/* Same orbit mark as the sidebar and OrbitLoader — ring with satellite at
   the top-left gap. Kept inline so the topbar can render without importing
   the sidebar. */
function BrandMark({ size = 28 }: { size?: number }) {
  const C = 2 * Math.PI * 44
  const gap = 50.8
  const dash = +(C - gap).toFixed(2)
  const dashOffset = 82
  return (
    <svg viewBox="0 0 128 128" width={size} height={size} fill="none" aria-hidden>
      <defs>
        <linearGradient id="ofMtBrandG" x1="19" y1="13" x2="109" y2="115" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.7" />
          <stop offset="55%"  stopColor="hsl(var(--primary))" stopOpacity="1" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
        </linearGradient>
      </defs>
      <circle
        cx="64" cy="64" r="44"
        stroke="url(#ofMtBrandG)"
        strokeWidth="16"
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={dashOffset}
      />
      <circle
        cx="29" cy="34" r="14"
        stroke="url(#ofMtBrandG)"
        strokeWidth="16"
      />
    </svg>
  )
}

export function MobileTopbar({ onMenuClick }: MobileTopbarProps) {
  return (
    <header className="of-mtopbar" aria-label="Mobile header">
      <button
        type="button"
        className="of-mtopbar-menu"
        onClick={onMenuClick}
        aria-label="Open navigation menu"
      >
        <Menu size={20} strokeWidth={1.8} />
      </button>
      <Link href="/" className="of-mtopbar-brand" aria-label="Orbit Flow home">
        <BrandMark size={26} />
        <span className="of-mtopbar-brand-name">Orbit Flow</span>
      </Link>
      {/* Reserve the right slot so the brand stays centered */}
      <span className="of-mtopbar-spacer" aria-hidden />
    </header>
  )
}
